import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import ProductsManager from './ProductsManager';
import InventoryManager from './InventoryManager';
import SuppliersManager from './SuppliersManager';
import LoadingSpinner from './LoadingSpinner';
import CompanyPlanCard from './CompanyPlanCard';
import './MyCompanyPage.css';
import AppIcon from './icons/AppIcon';

// Opções de segmento
const SEGMENTOS = [
  { value: '', label: 'Selecione um segmento' },
  { value: 'alimentacao', label: 'Alimentação' },
  { value: 'imoveis', label: 'Imóveis' },
  { value: 'tecnologia', label: 'Tecnologia' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'outros', label: 'Outros' },
];

// Subsegmentos por segmento
const SUBSEGMENTOS = {
  alimentacao: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'pizzaria', label: 'Pizzaria' },
    { value: 'lanchonete', label: 'Lanchonete' },
    { value: 'hamburgueria', label: 'Hamburgueria' },
    { value: 'comida_oriental', label: 'Comida Oriental' },
    { value: 'restaurante', label: 'Restaurante' },
  ],
  imoveis: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'imobiliaria', label: 'Imobiliária' },
    { value: 'corretor', label: 'Corretor' },
  ],
  tecnologia: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'servicos', label: 'Serviços' },
    { value: 'produtos_geral', label: 'Produtos em Geral' },
  ],
  ecommerce: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'moda', label: 'Moda' },
  ],
  outros: []
};

const MyCompanyPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('geral');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  // Estado do formulário - Informações Gerais
  const [companyForm, setCompanyForm] = useState({
    nome_fantasia: '',
    cnpj: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone_principal: '',
    telefone_secundario: '',
    email_contato: '',
    site: '',
    segmento: '',
    subsegmento: '',
    descricao: '',
    // Novos campos
    logo_url: '',
    logo_small_url: '',
    favicon_url: '',
    cor_primaria: '#6366f1',
    cor_secundaria: '#8b5cf6',
    cor_acento: '#10b981',
    timezone: 'America/Sao_Paulo',
    redes_sociais: {},
  });

  // Carregar dados da empresa ao montar o componente
  useEffect(() => {
    loadCompanyData();
  }, [user]);

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar company_id do usuário
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.company_id) {
        setError('Usuário não está vinculado a nenhuma empresa.');
        return;
      }

      setCompanyId(profile.company_id);

      // Buscar dados da empresa
      const { data: company, error: companyError } = await supabase
        .from('configuracoes_empresa')
        .select('*')
        .eq('company_id', profile.company_id)
        .single();

      if (companyError && companyError.code !== 'PGRST116') {
        throw companyError;
      }

      if (company) {
        setCompanyForm({
          nome_fantasia: company.nome_fantasia || '',
          cnpj: company.cnpj || '',
          logradouro: company.logradouro || '',
          numero: company.numero || '',
          complemento: company.complemento || '',
          bairro: company.bairro || '',
          cidade: company.cidade || '',
          estado: company.estado || '',
          cep: company.cep || '',
          telefone_principal: company.telefone_principal || '',
          telefone_secundario: company.telefone_secundario || '',
          email_contato: company.email_contato || '',
          site: company.site || '',
          segmento: company.segmento || '',
          subsegmento: company.subsegmento || '',
          descricao: company.descricao || '',
          logo_url: company.logo_url || '',
          logo_small_url: company.logo_small_url || '',
          favicon_url: company.favicon_url || '',
          cor_primaria: company.cor_primaria || '#6366f1',
          cor_secundaria: company.cor_secundaria || '#8b5cf6',
          cor_acento: company.cor_acento || '#10b981',
          timezone: company.timezone || 'America/Sao_Paulo',
          redes_sociais: company.redes_sociais || {},
        });
      }
    } catch (err) {
      console.error('Erro ao carregar dados da empresa:', err);
      setError('Erro ao carregar dados da empresa.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processed = value;

    // Formatação de CNPJ
    if (name === 'cnpj') {
      processed = value.replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/\.(\d{3})(\d)/, '.$1.$2')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }

    // Formatação de CEP
    if (name === 'cep') {
      processed = value.replace(/\D/g, '');
      if (processed.length > 5) {
        processed = processed.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
      }
      if (processed.replace(/\D/g, '').length === 8) {
        buscarEnderecoPorCEP(processed);
      }
    }

    // Limpar subsegmento quando trocar o segmento
    if (name === 'segmento') {
      setCompanyForm((prev) => ({ ...prev, [name]: processed, subsegmento: '' }));
      return;
    }

    setCompanyForm((prev) => ({ ...prev, [name]: processed }));
  };

  const buscarEnderecoPorCEP = async (cep) => {
    try {
      const onlyDigits = cep.replace(/\D/g, '');
      if (onlyDigits.length !== 8) return;
      
      setCepLoading(true);
      const res = await fetch(`https://viacep.com.br/ws/${onlyDigits}/json/`);
      const data = await res.json();
      
      if (!data.erro) {
        setCompanyForm((prev) => ({
          ...prev,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
          cep: onlyDigits.replace(/(\d{5})(\d{3})/, '$1-$2'),
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setCepLoading(false);
    }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (!companyId) {
        throw new Error('ID da empresa não encontrado.');
      }

      // Preparar dados para salvar
      const dataToSave = {
        company_id: companyId,
        nome_fantasia: companyForm.nome_fantasia,
        cnpj: companyForm.cnpj,
        logradouro: companyForm.logradouro,
        numero: companyForm.numero,
        complemento: companyForm.complemento,
        bairro: companyForm.bairro,
        cidade: companyForm.cidade,
        estado: companyForm.estado,
        cep: companyForm.cep,
        telefone_principal: companyForm.telefone_principal,
        telefone_secundario: companyForm.telefone_secundario,
        email_contato: companyForm.email_contato,
        site: companyForm.site,
        segmento: companyForm.segmento,
        subsegmento: companyForm.subsegmento,
        descricao: companyForm.descricao,
        logo_url: companyForm.logo_url,
        logo_small_url: companyForm.logo_small_url,
        favicon_url: companyForm.favicon_url,
        cor_primaria: companyForm.cor_primaria,
        cor_secundaria: companyForm.cor_secundaria,
        cor_acento: companyForm.cor_acento,
        timezone: companyForm.timezone,
        redes_sociais: companyForm.redes_sociais,
        updated_at: new Date().toISOString(),
      };

      // Upsert no Supabase
      const { error: saveError } = await supabase
        .from('configuracoes_empresa')
        .upsert(dataToSave, { onConflict: 'company_id' });

      if (saveError) throw saveError;

      setSuccess('✅ Informações salvas com sucesso!');
      
      // Recarregar dados após 2 segundos
      setTimeout(() => {
        setSuccess(null);
        loadCompanyData();
      }, 2000);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError(`Erro ao salvar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handler para atualizar redes sociais
  const handleSocialMediaChange = (platform, value) => {
    setCompanyForm((prev) => ({
      ...prev,
      redes_sociais: {
        ...prev.redes_sociais,
        [platform]: value,
      },
    }));
  };

  const renderGeneralTab = () => (
    <div className="my-company-tab-content">
      {/* Card do Plano */}
      <CompanyPlanCard />
      
      <form onSubmit={handleSaveGeneral}>
        {/* Dados Cadastrais */}
        <div className="settings-form-section">
          <h3>📋 Dados Cadastrais</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>
                Nome Fantasia *
                <span className="tooltip-icon" title="Nome comercial da sua empresa (como ela é conhecida no mercado)">
                  <AppIcon name="info" />
                </span>
              </label>
              <input
                type="text"
                name="nome_fantasia"
                value={companyForm.nome_fantasia}
                onChange={handleInputChange}
                placeholder="Nome da sua empresa"
                required
              />
            </div>
            
            <div className="form-group">
              <label>
                CNPJ
                <span className="tooltip-icon" title="Cadastro Nacional de Pessoa Jurídica (apenas números)">
                  <AppIcon name="info" />
                </span>
              </label>
              <input
                type="text"
                name="cnpj"
                value={companyForm.cnpj}
                onChange={handleInputChange}
                placeholder="00.000.000/0000-00"
                maxLength="18"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Segmento
                <span className="tooltip-icon" title="Selecione o segmento de atuação da sua empresa">
                  <AppIcon name="info" />
                </span>
              </label>
              <select
                name="segmento"
                value={companyForm.segmento}
                onChange={handleInputChange}
                className="select-field"
              >
                {SEGMENTOS.map((seg) => (
                  <option key={seg.value} value={seg.value}>
                    {seg.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Subsegmento - Aparece apenas se um segmento com subsegmentos foi selecionado */}
          {companyForm.segmento && SUBSEGMENTOS[companyForm.segmento]?.length > 0 && (
            <div className="form-row">
              <div className="form-group">
                <label>
                  Tipo de Negócio
                  <span className="tooltip-icon" title="Especifique o tipo específico do seu negócio">
                    <AppIcon name="info" />
                  </span>
                </label>
                <select
                  name="subsegmento"
                  value={companyForm.subsegmento}
                  onChange={handleInputChange}
                  className="select-field"
                >
                  {SUBSEGMENTOS[companyForm.segmento].map((subseg) => (
                    <option key={subseg.value} value={subseg.value}>
                      {subseg.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>
                Descrição
                <span className="tooltip-icon" title="⚠️ Importante: Descreva detalhadamente sua empresa, produtos e serviços. Esta informação será usada para configurações automáticas futuras do sistema.">
                  <AppIcon name="info" />
                </span>
              </label>
              <textarea
                name="descricao"
                value={companyForm.descricao}
                onChange={handleInputChange}
                placeholder="Descreva sua empresa..."
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="settings-form-section">
          <h3>📍 Endereço</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>
                CEP
                <span className="tooltip-icon" title="Digite o CEP e o endereço será preenchido automaticamente">
                  <AppIcon name="info" />
                </span>
              </label>
              <div className="cep-container">
                <input
                  type="text"
                  name="cep"
                  value={companyForm.cep}
                  onChange={handleInputChange}
                  placeholder="00000-000"
                  maxLength="9"
                />
                {cepLoading && <div className="loading-indicator"></div>}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 3 }}>
              <label>Logradouro</label>
              <input
                type="text"
                name="logradouro"
                value={companyForm.logradouro}
                onChange={handleInputChange}
                placeholder="Rua, Avenida..."
              />
            </div>
            
            <div className="form-group" style={{ flex: 1 }}>
              <label>Número</label>
              <input
                type="text"
                name="numero"
                value={companyForm.numero}
                onChange={handleInputChange}
                placeholder="Nº"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Complemento</label>
              <input
                type="text"
                name="complemento"
                value={companyForm.complemento}
                onChange={handleInputChange}
                placeholder="Sala, Andar..."
              />
            </div>
            
            <div className="form-group">
              <label>Bairro</label>
              <input
                type="text"
                name="bairro"
                value={companyForm.bairro}
                onChange={handleInputChange}
                placeholder="Bairro"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 3 }}>
              <label>Cidade</label>
              <input
                type="text"
                name="cidade"
                value={companyForm.cidade}
                onChange={handleInputChange}
                placeholder="Cidade"
              />
            </div>
            
            <div className="form-group" style={{ flex: 1 }}>
              <label>UF</label>
              <input
                type="text"
                name="estado"
                value={companyForm.estado}
                onChange={handleInputChange}
                placeholder="UF"
                maxLength="2"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>
        </div>

        {/* Contatos */}
        <div className="settings-form-section">
          <h3>📞 Contatos</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Telefone Principal</label>
              <input
                type="tel"
                name="telefone_principal"
                value={companyForm.telefone_principal}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
              />
            </div>
            
            <div className="form-group">
              <label>Telefone Secundário</label>
              <input
                type="tel"
                name="telefone_secundario"
                value={companyForm.telefone_secundario}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>E-mail de Contato</label>
              <input
                type="email"
                name="email_contato"
                value={companyForm.email_contato}
                onChange={handleInputChange}
                placeholder="contato@empresa.com.br"
              />
            </div>
            
            <div className="form-group">
              <label>
                Website
                <span className="tooltip-icon" title="Endereço do site da sua empresa (ex: www.empresa.com.br)">
                  <AppIcon name="info" />
                </span>
              </label>
              <input
                type="text"
                name="site"
                value={companyForm.site}
                onChange={handleInputChange}
                placeholder="www.empresa.com.br"
              />
            </div>
          </div>
        </div>

        {/* Identidade Visual */}
        <div className="settings-form-section">
          <h3>🎨 Identidade Visual</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Logo da Empresa</label>
              <div className="logo-upload-container">
                <input
                  type="text"
                  name="logo_url"
                  value={companyForm.logo_url}
                  onChange={handleInputChange}
                  placeholder="URL do logo ou clique para fazer upload"
                  readOnly
                />
                <button type="button" className="upload-button">
                  <AppIcon name="upload" />
                  Upload
                </button>
              </div>
              {companyForm.logo_url && (
                <div className="logo-preview">
                  <img src={companyForm.logo_url} alt="Logo Preview" />
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cor Primária</label>
              <div className="color-picker-container">
                <input
                  type="color"
                  name="cor_primaria"
                  value={companyForm.cor_primaria}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  value={companyForm.cor_primaria}
                  onChange={handleInputChange}
                  name="cor_primaria"
                  placeholder="#6366f1"
                  maxLength="7"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Cor Secundária</label>
              <div className="color-picker-container">
                <input
                  type="color"
                  name="cor_secundaria"
                  value={companyForm.cor_secundaria}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  value={companyForm.cor_secundaria}
                  onChange={handleInputChange}
                  name="cor_secundaria"
                  placeholder="#8b5cf6"
                  maxLength="7"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Redes Sociais */}
        <div className="settings-form-section">
          <h3>🌐 Redes Sociais</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>
                <AppIcon name="photo_camera" style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                {' '}Instagram
              </label>
              <input
                type="text"
                value={companyForm.redes_sociais?.instagram || ''}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                placeholder="@suaempresa"
              />
            </div>
            
            <div className="form-group">
              <label>
                <AppIcon name="facebook" style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                {' '}Facebook
              </label>
              <input
                type="text"
                value={companyForm.redes_sociais?.facebook || ''}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                placeholder="facebook.com/suaempresa"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <AppIcon name="work" style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                {' '}LinkedIn
              </label>
              <input
                type="text"
                value={companyForm.redes_sociais?.linkedin || ''}
                onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                placeholder="linkedin.com/company/suaempresa"
              />
            </div>
            
            <div className="form-group">
              <label>
                <AppIcon name="tag" style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                {' '}Twitter/X
              </label>
              <input
                type="text"
                value={companyForm.redes_sociais?.twitter || ''}
                onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                placeholder="@suaempresa"
              />
            </div>
          </div>
        </div>

        {/* Mensagens de Feedback */}
        {error && (
          <div className="alert alert-error">
            <AppIcon name="error" />
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <AppIcon name="check_circle" />
            {success}
          </div>
        )}

        {/* Ações */}
        <div className="form-actions">
          <button type="button" className="cancel-button">
            Cancelar
          </button>
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? (
              <>
                <AppIcon name="sync" className="spinning" />
                Salvando...
              </>
            ) : (
              <>
                <AppIcon name="save" />
                Salvar Alterações
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderProductsTab = () => {
    return <ProductsManager />;
  };

  const renderInventoryTab = () => {
    return <InventoryManager />;
  };

  const renderSuppliersTab = () => {
    return <SuppliersManager />;
  };

  // Loading state
  if (loading) {
    return (
      <div className="my-company-page">
        <div className="settings-content">
          <LoadingSpinner message="Carregando dados da empresa..." />
        </div>
      </div>
    );
  }

  return (
    <div className="my-company-page">
      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'geral' ? 'active' : ''}`}
          onClick={() => setActiveTab('geral')}
        >
          <AppIcon name="business" />
          Informações Gerais
        </button>
        <button
          className={`tab-button ${activeTab === 'produtos' ? 'active' : ''}`}
          onClick={() => setActiveTab('produtos')}
        >
          <AppIcon name="inventory_2" />
          Produtos
        </button>
        <button
          className={`tab-button ${activeTab === 'estoque' ? 'active' : ''}`}
          onClick={() => setActiveTab('estoque')}
        >
          <AppIcon name="warehouse" />
          Estoque
        </button>
        <button
          className={`tab-button ${activeTab === 'fornecedores' ? 'active' : ''}`}
          onClick={() => setActiveTab('fornecedores')}
        >
          <AppIcon name="local_shipping" />
          Fornecedores
        </button>
      </div>

      {/* Conteúdo */}
      <div className="settings-content">
        {activeTab === 'geral' && renderGeneralTab()}
        {activeTab === 'produtos' && renderProductsTab()}
        {activeTab === 'estoque' && renderInventoryTab()}
        {activeTab === 'fornecedores' && renderSuppliersTab()}
      </div>
    </div>
  );
};

export default MyCompanyPage;
